import { useEffect, useMemo, useRef, useState } from 'react';

import { apiClient } from '../../app/api/client';
import { Card, Page } from '../../app/layout/Page';
import { guided } from '../../app/guidance/fieldHelpRegistryCore';
import { simulationFieldHelp } from '../../app/guidance/fieldHelpRegistry';

import './simulation.css';

interface ScenarioOption {
  id: string;
  name: string;
}

export interface SimulationCandidateSite {
  allowed_arrangements: Array<'mixed' | 'parallel' | 'series'>;
  id: string;
  maximum_physical_units: number;
  name: string;
}

export interface SimulationFleetUnit {
  availability: string;
  id: string;
  maximum_engine_speed_rpm: number;
  minimum_engine_speed_rpm: number;
  name: string;
  optimization_eligible: boolean;
  physical_identifier: string;
  template_name: string;
}

interface SimulationRun {
  failure_code: string | null;
  id: string;
  input_hash: string;
  result: {
    converged: boolean;
    failures: Array<{ code: string; message: string }>;
    feasible: boolean;
  } | null;
  snapshot_failure_code: string | null;
  snapshot_state: 'failed' | 'not_requested' | 'saved';
  state: 'cancelled' | 'completed' | 'failed' | 'queued' | 'running';
}

interface PreparedRequest {
  input_hash: string;
  [key: string]: unknown;
}

interface UnitDraft {
  engineSpeedRpm: number;
  fleetUnitId: string;
  id: string;
  stageIndex: number;
}

interface BankDraft {
  arrangement: 'mixed' | 'parallel' | 'series';
  id: string;
  siteId: string;
  units: UnitDraft[];
}

export interface SimulationWorkspaceProps {
  candidateSites: SimulationCandidateSite[];
  fleetUnits: SimulationFleetUnit[];
  projectId: string;
  projectRevision: number;
  scenarios: ScenarioOption[];
}

function draftId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `bank-${Date.now()}-${Math.random()}`;
}

function speedFor(unit: SimulationFleetUnit | undefined): number {
  if (unit === undefined) return 1800;
  return Math.min(
    unit.maximum_engine_speed_rpm,
    Math.max(unit.minimum_engine_speed_rpm, 1800),
  );
}

function readableState(value: string): string {
  return value.replaceAll('_', ' ');
}

function outcomeFor(run: SimulationRun): {
  label: 'Needs changes' | 'Pass' | 'Running';
  tone: 'danger' | 'pending' | 'success';
} {
  if (run.result !== null) {
    return run.result.converged && run.result.feasible
      ? { label: 'Pass', tone: 'success' }
      : { label: 'Needs changes', tone: 'danger' };
  }
  if (run.state === 'queued' || run.state === 'running') {
    return { label: 'Running', tone: 'pending' };
  }
  return { label: 'Needs changes', tone: 'danger' };
}

function simulationContextKey({
  candidateSites,
  fleetUnits,
  projectId,
  projectRevision,
  scenarios,
}: SimulationWorkspaceProps): string {
  return JSON.stringify({
    candidateSites,
    fleetUnits,
    projectId,
    projectRevision,
    scenarios,
  });
}

export function SimulationWorkspace({
  candidateSites,
  fleetUnits,
  projectId,
  projectRevision,
  scenarios,
}: SimulationWorkspaceProps) {
  const defaultScenarioId = scenarios[0]?.id ?? '';
  const contextKey = simulationContextKey({
    candidateSites,
    fleetUnits,
    projectId,
    projectRevision,
    scenarios,
  });
  const [scenarioId, setScenarioId] = useState(defaultScenarioId);
  const [banks, setBanks] = useState<BankDraft[]>([]);
  const [diagnostic, setDiagnostic] = useState(false);
  const [prepared, setPrepared] = useState<PreparedRequest | null>(null);
  const [run, setRun] = useState<SimulationRun | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const contextKeyRef = useRef(contextKey);
  const previousContextKeyRef = useRef(contextKey);
  const operationGenerationRef = useRef(0);
  const mountedRef = useRef(true);
  contextKeyRef.current = contextKey;

  const active = run?.state === 'queued' || run?.state === 'running';
  const locked = busy || active;
  const eligibleUnits = useMemo(
    () =>
      fleetUnits.filter(
        (unit) => unit.availability === 'available' && unit.optimization_eligible,
      ),
    [fleetUnits],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      operationGenerationRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (previousContextKeyRef.current === contextKey) return;
    previousContextKeyRef.current = contextKey;
    operationGenerationRef.current += 1;
    setScenarioId(defaultScenarioId);
    setBanks([]);
    setDiagnostic(false);
    setPrepared(null);
    setRun(null);
    setBusy(false);
    setError(null);
  }, [contextKey, defaultScenarioId]);

  useEffect(() => {
    if (!active || run === null) return undefined;
    let ignore = false;
    const pollingContextKey = contextKey;
    const pollingGeneration = operationGenerationRef.current;
    const runId = run.id;
    const timer = window.setInterval(() => {
      void apiClient
        .get<SimulationRun>(`/simulations/runs/${runId}`)
        .then((nextRun) => {
          if (
            !ignore &&
            mountedRef.current &&
            operationGenerationRef.current === pollingGeneration &&
            contextKeyRef.current === pollingContextKey &&
            nextRun.id === runId
          ) {
            setRun(nextRun);
          }
        })
        .catch((caught: unknown) => {
          if (
            !ignore &&
            mountedRef.current &&
            operationGenerationRef.current === pollingGeneration &&
            contextKeyRef.current === pollingContextKey
          ) {
            setError(
              caught instanceof Error
                ? caught.message
                : 'Simulation status could not be refreshed.',
            );
          }
        });
    }, 500);
    return () => {
      ignore = true;
      window.clearInterval(timer);
    };
  }, [active, contextKey, run]);

  const clearCurrentResult = () => {
    operationGenerationRef.current += 1;
    setPrepared(null);
    setRun(null);
    setError(null);
  };

  const changeBanks = (next: BankDraft[]) => {
    setBanks(next);
    clearCurrentResult();
  };

  const addBank = () => {
    const site = candidateSites[0];
    const unit = eligibleUnits.find(
      (candidate) =>
        !banks.some((bank) =>
          bank.units.some((assigned) => assigned.fleetUnitId === candidate.id),
        ),
    );
    changeBanks([
      ...banks,
      {
        arrangement: site?.allowed_arrangements[0] ?? 'series',
        id: draftId(),
        siteId: site?.id ?? '',
        units:
          unit === undefined
            ? []
            : [
                {
                  engineSpeedRpm: speedFor(unit),
                  fleetUnitId: unit.id,
                  id: draftId(),
                  stageIndex: 0,
                },
              ],
      },
    ]);
  };

  const addUnit = (bankId: string) => {
    const assignedIds = new Set(
      banks.flatMap((bank) => bank.units.map((unit) => unit.fleetUnitId)),
    );
    const selected = eligibleUnits.find((unit) => !assignedIds.has(unit.id));
    changeBanks(
      banks.map((bank) =>
        bank.id === bankId
          ? {
              ...bank,
              units: [
                ...bank.units,
                {
                  engineSpeedRpm: speedFor(selected),
                  fleetUnitId: selected?.id ?? '',
                  id: draftId(),
                  stageIndex: bank.units.length,
                },
              ],
            }
          : bank,
      ),
    );
  };

  const pumpBankPayload = () => {
    const selectedIds = banks.flatMap((bank) =>
      bank.units.map((unit) => unit.fleetUnitId),
    );
    if (selectedIds.some((id) => id === '')) {
      throw new Error('Choose a named physical fleet unit for every bank row.');
    }
    if (new Set(selectedIds).size !== selectedIds.length) {
      throw new Error('One physical fleet unit cannot be assigned more than once.');
    }
    return banks.map((bank) => {
      const site = candidateSites.find((candidate) => candidate.id === bank.siteId);
      if (site === undefined) {
        throw new Error('Choose an approved candidate pump site for every bank.');
      }
      if (!site.allowed_arrangements.includes(bank.arrangement)) {
        throw new Error(`${site.name} does not permit a ${bank.arrangement} bank.`);
      }
      if (bank.units.length === 0) {
        throw new Error(`Add at least one physical unit at ${site.name}.`);
      }
      if (bank.units.length > site.maximum_physical_units) {
        throw new Error(
          `${site.name} allows ${site.maximum_physical_units} physical ${site.maximum_physical_units === 1 ? 'unit' : 'units'}, but ${bank.units.length} are assigned.`,
        );
      }
      for (const assigned of bank.units) {
        const unit = fleetUnits.find((candidate) => candidate.id === assigned.fleetUnitId);
        if (
          unit === undefined ||
          unit.availability !== 'available' ||
          !unit.optimization_eligible
        ) {
          throw new Error(
            'A selected fleet unit is no longer available with a verified curve.',
          );
        }
        if (
          !Number.isInteger(assigned.engineSpeedRpm) ||
          assigned.engineSpeedRpm < unit.minimum_engine_speed_rpm ||
          assigned.engineSpeedRpm > unit.maximum_engine_speed_rpm
        ) {
          throw new Error(
            `${unit.physical_identifier} speed must be an integer from ${unit.minimum_engine_speed_rpm} through ${unit.maximum_engine_speed_rpm} RPM.`,
          );
        }
      }
      const byStage = new Map<number, UnitDraft[]>();
      bank.units.forEach((unit, index) => {
        const stage =
          bank.arrangement === 'parallel'
            ? 0
            : bank.arrangement === 'series'
              ? index
              : unit.stageIndex;
        byStage.set(stage, [...(byStage.get(stage) ?? []), unit]);
      });
      const orderedStages = [...byStage.entries()].sort(
        ([left], [right]) => left - right,
      );
      if (orderedStages.some(([stage], index) => stage !== index)) {
        throw new Error(`Mixed-bank stages at ${site.name} must be contiguous from zero.`);
      }
      return {
        arrangement: bank.arrangement,
        candidate_site_id: bank.siteId,
        stages: orderedStages.map(([stageIndex, units]) => ({
          stage_index: stageIndex,
          units: units.map((unit) => ({
            enabled: true,
            engine_speed_rpm: unit.engineSpeedRpm,
            fleet_unit_id: unit.fleetUnitId,
          })),
        })),
      };
    });
  };

  async function start() {
    const operationGeneration = operationGenerationRef.current + 1;
    operationGenerationRef.current = operationGeneration;
    const requestedContextKey = contextKey;
    setBusy(true);
    setError(null);
    setPrepared(null);
    setRun(null);
    try {
      const request = await apiClient.post<PreparedRequest>(
        '/workflow/simulation-request',
        {
          json: {
            enable_pressure_dependent_diagnostic: diagnostic,
            expected_project_revision: projectRevision,
            project_id: projectId,
            pump_banks: pumpBankPayload(),
            scenario_id: scenarioId,
          },
        },
      );
      if (
        !mountedRef.current ||
        operationGenerationRef.current !== operationGeneration ||
        contextKeyRef.current !== requestedContextKey
      ) {
        return;
      }
      setPrepared(request);
      const accepted = await apiClient.post<{ run_id: string }>(
        '/simulations/runs',
        { json: request },
      );
      if (
        !mountedRef.current ||
        operationGenerationRef.current !== operationGeneration ||
        contextKeyRef.current !== requestedContextKey
      ) {
        return;
      }
      const nextRun = await apiClient.get<SimulationRun>(
        `/simulations/runs/${accepted.run_id}`,
      );
      if (
        mountedRef.current &&
        operationGenerationRef.current === operationGeneration &&
        contextKeyRef.current === requestedContextKey &&
        nextRun.id === accepted.run_id
      ) {
        setRun(nextRun);
      }
    } catch (caught) {
      if (
        mountedRef.current &&
        operationGenerationRef.current === operationGeneration &&
        contextKeyRef.current === requestedContextKey
      ) {
        setError(
          caught instanceof Error ? caught.message : 'Simulation could not be run.',
        );
      }
    } finally {
      if (
        mountedRef.current &&
        operationGenerationRef.current === operationGeneration &&
        contextKeyRef.current === requestedContextKey
      ) {
        setBusy(false);
      }
    }
  }

  const outcome = run === null ? null : outcomeFor(run);

  return (
    <Page title="Simulation">
      <div className="simulation-workspace">
      {scenarios.length === 0 ? (
        <Card title="No scenarios">
          <p>Add a scenario before simulation.</p>
        </Card>
      ) : (
        <Card title="Scenario and pump plan">
          <label className="simulation-field">
            Scenario
            <select
              {...guided(simulationFieldHelp.scenario)}
              disabled={locked}
              value={scenarioId}
              onChange={(event) => {
                setScenarioId(event.currentTarget.value);
                clearCurrentResult();
              }}
            >
              {scenarios.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <section className="simulation-plan" aria-labelledby="pump-bank-title">
            <h3 id="pump-bank-title">Pump plan</h3>
            <p>Leave empty for a no-pump check.</p>
            {candidateSites.length === 0 ? (
              <p role="note">No approved pump sites.</p>
            ) : null}
            {eligibleUnits.length === 0 ? (
              <p role="note">No available units with verified curves.</p>
            ) : null}
            <div className="simulation-banks">
            {banks.map((bank, bankIndex) => {
              const site = candidateSites.find((candidate) => candidate.id === bank.siteId);
              const arrangements = site?.allowed_arrangements ?? [];
              return (
                <fieldset className="simulation-bank" key={bank.id}>
                  <legend>Bank {bankIndex + 1}</legend>
                  <label>
                    Approved site
                    <select
                      {...guided(simulationFieldHelp.site, bank.id)}
                      disabled={locked}
                      aria-label={`Approved site for bank ${bankIndex + 1}`}
                      value={bank.siteId}
                      onChange={(event) => {
                        const nextSite = candidateSites.find(
                          (candidate) => candidate.id === event.currentTarget.value,
                        );
                        changeBanks(
                          banks.map((candidate) =>
                            candidate.id === bank.id
                              ? {
                                  ...candidate,
                                  arrangement:
                                    nextSite?.allowed_arrangements.includes(candidate.arrangement) === true
                                      ? candidate.arrangement
                                      : (nextSite?.allowed_arrangements[0] ?? 'series'),
                                  siteId: event.currentTarget.value,
                                }
                              : candidate,
                          ),
                        );
                      }}
                    >
                      <option value="">Choose an approved site</option>
                      {candidateSites.map((candidate) => (
                        <option key={candidate.id} value={candidate.id}>
                          {candidate.name} — capacity {candidate.maximum_physical_units}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Arrangement
                    <select
                      {...guided(simulationFieldHelp.arrangement, bank.id)}
                      disabled={locked}
                      aria-label={`Arrangement for bank ${bankIndex + 1}`}
                      value={bank.arrangement}
                      onChange={(event) =>
                        changeBanks(
                          banks.map((candidate) =>
                            candidate.id === bank.id
                              ? {
                                  ...candidate,
                                  arrangement: event.currentTarget.value as BankDraft['arrangement'],
                                }
                              : candidate,
                          ),
                        )
                      }
                    >
                      {arrangements.map((arrangement) => (
                        <option key={arrangement} value={arrangement}>
                          {arrangement[0]?.toUpperCase()}{arrangement.slice(1)}
                        </option>
                      ))}
                    </select>
                  </label>
                  {bank.units.map((assigned, unitIndex) => {
                    const selected = fleetUnits.find(
                      (candidate) => candidate.id === assigned.fleetUnitId,
                    );
                    return (
                      <fieldset className="simulation-unit" key={assigned.id}>
                        <legend>Physical unit {unitIndex + 1}</legend>
                        <label>
                          Fleet unit
                          <select {...guided(simulationFieldHelp.fleetUnit, `${bank.id}:${assigned.id}`)}
                            aria-label={`Fleet unit ${unitIndex + 1} for bank ${bankIndex + 1}`}
                            disabled={locked}
                            value={assigned.fleetUnitId}
                            onChange={(event) => {
                              const nextUnit = fleetUnits.find(
                                (candidate) => candidate.id === event.currentTarget.value,
                              );
                              changeBanks(
                                banks.map((candidate) =>
                                  candidate.id === bank.id
                                    ? {
                                        ...candidate,
                                        units: candidate.units.map((unit, index) =>
                                          index === unitIndex
                                            ? {
                                                ...unit,
                                                engineSpeedRpm: speedFor(nextUnit),
                                                fleetUnitId: event.currentTarget.value,
                                              }
                                            : unit,
                                        ),
                                      }
                                    : candidate,
                                ),
                              );
                            }}
                          >
                            <option value="">Choose a physical fleet unit</option>
                            {fleetUnits.map((unit) => (
                              <option
                                disabled={
                                  unit.availability !== 'available' ||
                                  !unit.optimization_eligible
                                }
                                key={unit.id}
                                value={unit.id}
                              >
                                {unit.physical_identifier} — {unit.name} — {unit.template_name}
                                {unit.availability !== 'available'
                                  ? ` (${unit.availability})`
                                  : !unit.optimization_eligible
                                    ? ' (verified curve required)'
                                    : ''}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Engine speed for {selected?.physical_identifier ?? `unit ${unitIndex + 1}`} (RPM)
                          <input {...guided(simulationFieldHelp.engineSpeed, `${bank.id}:${assigned.id}`)}
                            aria-label={`Engine speed for unit ${unitIndex + 1} in bank ${bankIndex + 1}`}
                            disabled={locked}
                            max={selected?.maximum_engine_speed_rpm}
                            min={selected?.minimum_engine_speed_rpm}
                            step="1"
                            type="number"
                            value={assigned.engineSpeedRpm}
                            onChange={(event) =>
                              changeBanks(
                                banks.map((candidate) =>
                                  candidate.id === bank.id
                                    ? {
                                        ...candidate,
                                        units: candidate.units.map((unit, index) =>
                                          index === unitIndex
                                            ? {
                                                ...unit,
                                                engineSpeedRpm: event.currentTarget.valueAsNumber,
                                              }
                                            : unit,
                                        ),
                                      }
                                    : candidate,
                                ),
                              )
                            }
                          />
                        </label>
                        {bank.arrangement === 'mixed' ? (
                          <label>
                            Series stage (zero-based)
                            <input {...guided(simulationFieldHelp.seriesStage, `${bank.id}:${assigned.id}`)}
                              aria-label={`Stage for unit ${unitIndex + 1} in bank ${bankIndex + 1}`}
                              disabled={locked}
                              min="0"
                              step="1"
                              type="number"
                              value={assigned.stageIndex}
                              onChange={(event) =>
                                changeBanks(
                                  banks.map((candidate) =>
                                    candidate.id === bank.id
                                      ? {
                                          ...candidate,
                                          units: candidate.units.map((unit, index) =>
                                            index === unitIndex
                                              ? {
                                                  ...unit,
                                                  stageIndex: event.currentTarget.valueAsNumber,
                                                }
                                              : unit,
                                          ),
                                        }
                                      : candidate,
                                  ),
                                )
                              }
                            />
                          </label>
                        ) : null}
                        <button
                          disabled={locked}
                          type="button"
                          onClick={() =>
                            changeBanks(
                              banks.map((candidate) =>
                                candidate.id === bank.id
                                  ? {
                                      ...candidate,
                                      units: candidate.units.filter(
                                        (_unit, index) => index !== unitIndex,
                                      ),
                                    }
                                  : candidate,
                              ),
                            )
                          }
                        >
                          Remove {selected?.physical_identifier ?? `unit ${unitIndex + 1}`}
                        </button>
                      </fieldset>
                    );
                  })}
                  <button
                    disabled={
                      locked ||
                      bank.units.length >= (site?.maximum_physical_units ?? 0)
                    }
                    type="button"
                    onClick={() => addUnit(bank.id)}
                  >
                    Add unit
                  </button>{' '}
                  <button
                    disabled={locked}
                    type="button"
                    onClick={() => changeBanks(banks.filter((candidate) => candidate.id !== bank.id))}
                  >
                    Remove bank
                  </button>
                </fieldset>
              );
            })}
            </div>
            <button
              disabled={
                locked ||
                candidateSites.length === 0 ||
                eligibleUnits.length === 0
              }
              type="button"
              onClick={addBank}
            >
              Add pump bank
            </button>
          </section>
          <details className="simulation-options">
            <summary>Diagnostics</summary>
            <label>
              <input
                {...guided(simulationFieldHelp.pddDiagnostic)}
                checked={diagnostic}
                disabled={locked}
                type="checkbox"
                onChange={(event) => {
                  setDiagnostic(event.currentTarget.checked);
                  clearCurrentResult();
                }}
              />
              Pressure-dependent diagnostic
            </label>
          </details>
          <button
            className="button button--primary simulation-run-action"
            disabled={locked}
            type="button"
            onClick={() => void start()}
          >
            {busy ? 'Running…' : 'Run simulation'}
          </button>
          {error === null ? null : <p role="alert">{error}</p>}
        </Card>
      )}
      {run === null || outcome === null ? null : (
        <Card
          className={`simulation-result simulation-result--${outcome.tone}`}
        >
          <div aria-live="polite" role="status">
            <h2 className="simulation-result__outcome">{outcome.label}</h2>
          </div>
          {active ? (
            <progress aria-label="Simulation progress" />
          ) : run.result === null ? (
            <p>No result was returned.</p>
          ) : run.result.failures.length > 0 ? (
            <section aria-labelledby="simulation-limits-title">
              <h3 id="simulation-limits-title">Limiting checks</h3>
              <ul className="simulation-failures">
                {run.result.failures.map((failure) => (
                  <li key={`${failure.code}-${failure.message}`}>
                    <strong>{readableState(failure.code)}</strong>
                    <span>{failure.message}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : !run.result.converged ? (
            <p>Solver did not converge.</p>
          ) : !run.result.feasible ? (
            <p>No limiting check was reported.</p>
          ) : null}
          {run.snapshot_state === 'saved' ? (
            <p className="simulation-saved" role="status">
              Inputs and result saved.
            </p>
          ) : null}
          {run.snapshot_state === 'failed' ? (
            <p role="alert">Result saved; portable snapshot capture failed.</p>
          ) : null}
        </Card>
      )}
      {prepared === null && run === null ? null : (
        <details className="card simulation-details">
          <summary>Run details</summary>
          <dl>
            <div>
              <dt>Input hash</dt>
              <dd>
                <code>{run?.input_hash ?? prepared?.input_hash}</code>
              </dd>
            </div>
            {run === null ? null : (
              <>
                <div>
                  <dt>Run ID</dt>
                  <dd>{run.id}</dd>
                </div>
                <div>
                  <dt>Run state</dt>
                  <dd>{readableState(run.state)}</dd>
                </div>
                <div>
                  <dt>Converged</dt>
                  <dd>
                    {run.result === null
                      ? 'not evaluated'
                      : run.result.converged
                        ? 'yes'
                        : 'no'}
                  </dd>
                </div>
                <div>
                  <dt>Snapshot</dt>
                  <dd>{readableState(run.snapshot_state)}</dd>
                </div>
                {run.failure_code === null ? null : (
                  <div>
                    <dt>Run diagnostic</dt>
                    <dd>{readableState(run.failure_code)}</dd>
                  </div>
                )}
                {run.snapshot_failure_code === null ? null : (
                  <div>
                    <dt>Snapshot diagnostic</dt>
                    <dd>{readableState(run.snapshot_failure_code)}</dd>
                  </div>
                )}
              </>
            )}
          </dl>
        </details>
      )}
      </div>
    </Page>
  );
}
